/**
 * Sysadmin Runtime → Hardware (Phase 5 PR-D v1).
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.6 + Phase 5 architect §2.4:
 *   Replaces the old "redirect to /sysadmin/models/runtime" stub. This
 *   page is the universal "what does this box have?" sub-tab of the
 *   Runtime rail and renders on EVERY deployment profile (CPU-only,
 *   Strix Halo iGPU, NVIDIA, edge) — independent of the Models pipeline.
 *
 *   The richer Models > Runtime page still owns engine/gateway
 *   internals (backend_model_name, baseLlmKey) per I-M6.7; that
 *   surface remains at /sysadmin/models/runtime.
 *
 * Refresh: 10s default via createSysadminQuery — matches the rest of
 * the sysadmin runtime surface so every operational panel ticks
 * together.
 */

import { createFileRoute } from '@tanstack/react-router';
import {
  HardDrive,
  Cpu,
  MemoryStick,
  Activity,
  Thermometer,
  Zap,
  RefreshCw,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusPill } from '@/components/ui/status-pill';
import { PageError } from '@/components/ui/page-error';
import { Progress } from '@/components/ui/progress';
import {
  useSysadminRuntimeHardware,
  type GpuInfoRow,
} from '@/lib/use-sysadmin-runtime-ops';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/runtime/hardware')({
  component: HardwarePage,
});

function HardwarePage() {
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } =
    useSysadminRuntimeHardware();

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load hardware snapshot"
          message={error.message}
          onRetry={() => refetch()}
          retrying={isFetching}
        />
      </div>
    );
  }

  const host = data?.host;
  const gpu = data?.gpu;
  const memoryPercent =
    host && host.memoryTotalMb && host.memoryUsedMb
      ? Math.round((host.memoryUsedMb / host.memoryTotalMb) * 100)
      : null;
  const diskPercent =
    host && host.diskTotalGb && host.diskUsedGb
      ? Math.round((host.diskUsedGb / host.diskTotalGb) * 100)
      : null;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <HardDrive className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Hardware</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.6</Badge>
              <Badge variant="secondary" className="text-xs">PR-D</Badge>
              {dataUpdatedAt > 0 && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5 mr-1.5', isFetching && 'animate-spin')}
          />
          Refresh
        </Button>
      </div>

      {isLoading && !data ? (
        <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
          Probing hardware…
        </div>
      ) : (
        <>
          {/* ── Top 4-up summary ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile
              label="GPUs"
              value={gpu?.gpus.length ?? 0}
              suffix={gpu?.available ? '' : ' (none)'}
              icon={Zap}
              tone={gpu?.available && gpu.gpus.length > 0 ? 'success' : 'muted'}
              hint={
                gpu?.unavailableReason
                  ? gpu.unavailableReason
                  : gpu?.gpus[0]?.name ?? 'CPU-only host'
              }
            />
            <StatTile
              label="CPU threads"
              value={host?.cpuCores ?? '—'}
              icon={Cpu}
              tone={host?.available ? 'default' : 'muted'}
              hint={host?.cpuModel ?? 'unknown CPU'}
            />
            <StatTile
              label="RAM used"
              value={
                host?.memoryUsedMb != null
                  ? `${(host.memoryUsedMb / 1024).toFixed(1)} GB`
                  : '—'
              }
              suffix={
                host?.memoryTotalMb != null
                  ? `/ ${(host.memoryTotalMb / 1024).toFixed(0)} GB`
                  : undefined
              }
              icon={MemoryStick}
              tone={
                memoryPercent !== null && memoryPercent > 90
                  ? 'destructive'
                  : memoryPercent !== null && memoryPercent > 75
                    ? 'warning'
                    : 'default'
              }
              hint={memoryPercent !== null ? `${memoryPercent}%` : undefined}
            />
            <StatTile
              label="Disk free"
              value={
                host?.diskFreeGb != null ? `${host.diskFreeGb} GB` : '—'
              }
              suffix={
                host?.diskTotalGb != null
                  ? `/ ${host.diskTotalGb} GB`
                  : undefined
              }
              icon={HardDrive}
              tone={
                diskPercent !== null && diskPercent > 90
                  ? 'destructive'
                  : diskPercent !== null && diskPercent > 75
                    ? 'warning'
                    : 'success'
              }
              hint={diskPercent !== null ? `${diskPercent}% used` : undefined}
            />
          </div>

          {/* ── Host detail ── */}
          {host && (
            <section className="space-y-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <Cpu className="h-4 w-4 text-muted-foreground" />
                Host
                {!host.available && (
                  <StatusPill status="muted" compact icon={AlertTriangle}>
                    Probe degraded
                  </StatusPill>
                )}
              </h2>
              <div className="rounded-md border p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <KvRow label="Hostname" value={host.hostname} mono />
                <KvRow label="OS" value={host.os} />
                <KvRow label="Kernel" value={host.kernel} mono />
                <KvRow
                  label="Uptime"
                  value={formatUptime(host.uptimeSeconds)}
                />
                <KvRow
                  label="Load (1/5/15m)"
                  value={host.loadAvg.map((n) => n.toFixed(2)).join(' / ')}
                  mono
                />
                <KvRow
                  label="Memory"
                  value={
                    host.memoryUsedMb != null && host.memoryTotalMb != null
                      ? `${(host.memoryUsedMb / 1024).toFixed(1)} / ${(host.memoryTotalMb / 1024).toFixed(0)} GB`
                      : '—'
                  }
                />
              </div>
              {memoryPercent !== null && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">RAM usage</span>
                    <span className="font-mono tabular-nums">
                      {memoryPercent}%
                    </span>
                  </div>
                  <Progress value={memoryPercent} className="h-1.5" />
                </div>
              )}
              {diskPercent !== null && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Disk usage</span>
                    <span className="font-mono tabular-nums">
                      {diskPercent}%
                    </span>
                  </div>
                  <Progress value={diskPercent} className="h-1.5" />
                </div>
              )}
              {host.notes.length > 0 && (
                <div className="text-[11px] text-muted-foreground border-l-2 border-amber-500/40 pl-2 py-1">
                  {host.notes.join(' · ')}
                </div>
              )}
            </section>
          )}

          {/* ── Per-GPU detail ── */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Zap className="h-4 w-4 text-muted-foreground" />
              GPUs
              {gpu && !gpu.available && (
                <StatusPill status="muted" compact>
                  {gpu.unavailableReason ?? 'No GPUs detected'}
                </StatusPill>
              )}
            </h2>
            {gpu && gpu.gpus.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {gpu.gpus.map((g) => (
                  <GpuTile key={g.index} gpu={g} />
                ))}
              </div>
            ) : (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                {gpu?.unavailableReason ??
                  'No GPUs detected — CPU-only deployment.'}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

// ─── Atoms ───────────────────────────────────────────────────────────────

function KvRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="space-y-0.5">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={cn('text-sm', mono && 'font-mono')}>{value}</div>
    </div>
  );
}

function GpuTile({ gpu }: { gpu: GpuInfoRow }) {
  const vramPercent =
    gpu.vramTotalMb && gpu.vramUsedMb
      ? Math.round((gpu.vramUsedMb / gpu.vramTotalMb) * 100)
      : null;
  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm truncate" title={gpu.name}>
            GPU {gpu.index} · {gpu.name}
          </div>
          {gpu.vramTotalMb != null && (
            <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
              {gpu.vramTotalMb} MB VRAM
            </div>
          )}
        </div>
        {gpu.temperatureC != null && (
          <Badge
            variant="outline"
            className={cn(
              'text-[10px] gap-1 shrink-0',
              gpu.temperatureC > 85
                ? 'text-destructive'
                : gpu.temperatureC > 75
                  ? 'text-amber-600'
                  : '',
            )}
          >
            <Thermometer className="h-3 w-3" />
            {gpu.temperatureC}°C
          </Badge>
        )}
      </div>

      {vramPercent !== null && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">VRAM</span>
            <span className="font-mono tabular-nums">
              {gpu.vramUsedMb} / {gpu.vramTotalMb} MB · {vramPercent}%
            </span>
          </div>
          <Progress value={vramPercent} className="h-1.5" />
        </div>
      )}

      {gpu.utilizationPercent != null && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              <Activity className="h-3 w-3 inline mr-1" />
              Util
            </span>
            <span className="font-mono tabular-nums">
              {gpu.utilizationPercent}%
            </span>
          </div>
          <Progress value={gpu.utilizationPercent} className="h-1.5" />
        </div>
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const mins = Math.floor((seconds % 3_600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}
